import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  layout,
  LayoutProps,
  color,
  ColorProps,
  flexbox,
  FlexboxProps,
  space,
  SpaceProps,
  position,
  PositionProps,
} from 'styled-system';
import Hamburger from 'hamburger-react';
import Link from '../Link/Link';
import { useLocation, matchPath } from 'react-router-dom';
import {
  mobileBreakpoint,
  useIsMobile,
} from '@keyslabor/shared/hooks/useBreakPoint';
import { Divider, Flex, Grid } from '@keyslabor/shared/ui';
import { px } from '@/utils';
import useConfirmLogout from './Modals/useConfirmLogout';
import PropertySelect from '@/components/PropertySelect/PropertySelect';
import { UnsavedScheduleTracking } from '@/components/PageLayout/PageLayout';
import { useNavigationMenuItems } from '@/components/Header/useNavigationMenuItems.ts';
import { useProfileMenuItems } from '@/components/Header/useProfileMenuItems.ts';
import { MenuItemConfig } from '@/components/Header/types.ts';
import { Routes } from '@/globalTypes.ts';
import { ChevronLeftIcon } from 'lucide-react';
import { Link as TextLink } from '@keyslabor/shared/ui';
import { cn } from '@/lib/utils.ts';
import { useIsInterviewOnly } from '@/hooks/useIsInterviewOnly.ts';

interface Props {
  unsavedScheduleTracking?: UnsavedScheduleTracking;
  showBackButton?: boolean;
}

export default function Header({
  unsavedScheduleTracking,
  showBackButton,
}: Props) {
  const [isOpen, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [renderConfirmLogout, toggleConfirmLogout] = useConfirmLogout();
  const isInterviewOnly = useIsInterviewOnly();
  const navigationMenuItems = useNavigationMenuItems();
  const profileMenuItems = useProfileMenuItems({
    onLogout: toggleConfirmLogout,
  });

  const handleIsOpen = (isOpen: boolean) => {
    if (isOpen) {
      document.getElementsByTagName('body')[0].style.height = '100vh';
      document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    } else {
      document.getElementsByTagName('body')[0].style.height = '';
      document.getElementsByTagName('body')[0].style.overflow = '';
    }
    setOpen(isOpen);
  };

  useEffect(() => {
    return () => {
      handleIsOpen(false);
    };
  }, []);

  const items = (
    <>
      <Grid gridAutoFlow={isMobile ? 'row' : 'column'}>
        {navigationMenuItems.map((item) => (
          <HeaderMenuItem
            key={item.pathname}
            item={item}
            unsavedScheduleTracking={unsavedScheduleTracking}
          />
        ))}
      </Grid>
      {isMobile && !!navigationMenuItems.length && <Divider mt="26px" />}
      <Grid gridAutoFlow={isMobile ? 'row' : 'column'}>
        {profileMenuItems.map((item) => (
          <HeaderMenuItem
            key={item.pathname}
            item={item}
            unsavedScheduleTracking={unsavedScheduleTracking}
          />
        ))}
      </Grid>
    </>
  );

  return (
    <Container>
      <Wrapper>
        <TextLink
          to={isInterviewOnly ? Routes.LeadsInhouse : Routes.LeadsByAgency}
          className={cn(
            'bg-[#143980] rounded-full uppercase px-2 text-white hidden items-center',
            {
              'flex md:hidden': showBackButton,
              'md:flex': showBackButton && isInterviewOnly,
            }
          )}
          css={`
            color: white;
          `}
        >
          <ChevronLeftIcon />
          Go back
        </TextLink>
        <div
          className={cn('flex items-center', {
            'hidden md:flex': showBackButton,
            'md:hidden': showBackButton && isInterviewOnly,
          })}
        >
          <PropertySelect />
        </div>

        {!isMobile && items}

        {isMobile && (
          <Flex width="100%" justifyContent="flex-end">
            <MobileMenu isOpen={isOpen}>
              <MobileMenuWrapper>{items}</MobileMenuWrapper>
            </MobileMenu>
            <HamburgerContainer>
              <Hamburger
                toggled={isOpen}
                toggle={handleIsOpen}
                size={22}
                color="#143980"
              />
            </HamburgerContainer>
          </Flex>
        )}
      </Wrapper>
      {isMobile && (
        <Divider height="1px" width="calc(100% - 20px)" m="0 auto" />
      )}
      {renderConfirmLogout}
    </Container>
  );
}

const HeaderMenuItem = ({
  item,
  unsavedScheduleTracking,
}: {
  item: MenuItemConfig;
  unsavedScheduleTracking?: UnsavedScheduleTracking;
}) => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  return (
    <MenuItem>
      <Link
        unsavedScheduleTracking={unsavedScheduleTracking}
        isActive={
          item.routePattern
            ? !!matchPath(item.routePattern, pathname)
            : pathname === item.pathname
        }
        to={item.onClick ? undefined : item.pathname}
        onClick={item.onClick}
        label={item.label}
        {...(item.styles ? item.styles : {})}
        {...(isMobile && item.mobileStyles ? item.mobileStyles : {})}
      />
    </MenuItem>
  );
};

const Container = styled.div<
  LayoutProps | ColorProps | FlexboxProps | SpaceProps | PositionProps
>`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;

  ${layout}
  ${color}
  ${flexbox}
  ${space}
  ${position}
`;

const Wrapper = styled.div`
  width: 100%;
  height: 50px;
  background-color: #fff;
  padding: 0px 50px 0px 50px;
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content max-content max-content;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: ${px(mobileBreakpoint)}) {
    padding: 0px 20px 0px 20px;
  }
`;

const Box = styled.div<LayoutProps | ColorProps | FlexboxProps | SpaceProps>`
  ${layout}
  ${color}
  ${flexbox}
  ${space}
`;

const MenuItem = styled.div<
  LayoutProps | ColorProps | FlexboxProps | SpaceProps
>`
  ${layout}
  ${color}
  ${flexbox}
  ${space}

  cursor: pointer;
`;

const HamburgerContainer = styled.div`
  display: none;

  @media screen and (max-width: ${px(mobileBreakpoint)}) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: ${({ isOpen }) => (isOpen ? '100vh' : '0px')};
  display: none;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  background: #fff;
  transition: 0.4s;
  @media screen and (max-width: ${px(mobileBreakpoint)}) {
    display: block;
  }
`;

const MobileMenuWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 70px 35px 35px 35px;
  list-style: none;
`;
